/**
 * Heavily based off of the 'observable-profiler' library
 * Added ability to ignore keywords within the error stack.
 * - if a pattern is matched the complete stack (grouped by error id) is removed.
 * Ref: https://github.com/awerlang/observable-profiler/blob/master/src/index.ts
 */

/**
 * Represents the source of an subscription
 */
export class SubscriptionSource extends Error {
  /**
   * @param subscription A subscription to an Observable
   * @param id The id of the subscription chain
   */
  constructor(public readonly subscription: object, public readonly id: number) {
    super();
  }
}

let subscribersGlobal: Set<SubscriptionSource>;
let isTracking = false;

/**
 * Call setup() once before any calls to track()
 * @param Observable Bring your own Observable class to track
 */
export function setup<T>(Observable: any) {
  // eslint-disable-next-line no-console
  console.warn(
    '================== Running Observable Tracker ==================\n  !!!!!! Be aware this will affect application performance. !!!!!!',
  );
  const origSubscribe = Observable.prototype.subscribe;
  Observable.prototype.subscribe = subscribe;

  let id = 0;
  let root = false;
  function subscribe(this: any, ...args: any[]) {
    let setRoot = false;
    if (!root) {
      setRoot = true;
      root = true;
      id++;
    }
    const subscription = origSubscribe.apply(this, args);
    if (isTracking) {
      const currentSubscribers = subscribersGlobal;
      const sub = new SubscriptionSource(subscription, id);
      currentSubscribers.add(sub);
      subscription.add(() => {
        currentSubscribers.delete(sub);
      });
    }
    if (setRoot) {
      root = false;
    }
    return subscription;
  }
}

/**
 * Accessor to the current subscription list.
 */
export class Iterator {
  constructor(private readonly subscribers: Set<SubscriptionSource>) {}

  /**
   * Returns a snapshot of current subscriptions
   */
  current() {
    return [...this.subscribers];
  }
}

/**
 * Returns a snapshot of current subscriptions since tracking started
 */
export function getSubscribers() {
  return new Iterator(subscribersGlobal);
}

/**
 * Starts/stops tracking of Observable subscriptions
 * @param canTrack `true` to start; `false` to stop
 */
export function track(canTrack = true) {
  if (isTracking === canTrack) {
    return;
  }
  isTracking = canTrack;
  if (canTrack) {
    subscribersGlobal = new Set();
  }
  return getSubscribers();
}

function delay(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Outputs to console the list of active subscriptions
 * @param prefix Prints a prefix on each tracked subscription
 * @param timeout Give some leeway (in ms) for time-based subscriptions to finish
 * @param rewriteStack `true` to remove some noise from stack traces
 * @param filterStackRe a custom Regexp object to filter stack frames
 * @param reportInnerSubscriptions `true` to report indirect subscriptions
 * @param subscribers The result of a previous call to `track(false)`
 */
export async function printSubscribers({
  prefix = '',
  timeout = 0,
  rewriteStack = false,
  filterStackRe = undefined,
  reportInnerSubscriptions = false,
  ignoreStackFilterRe = undefined,
  subscribers = undefined,
}: {
  prefix?: string;
  timeout?: number;
  rewriteStack?: boolean;
  filterStackRe?: RegExp;
  reportInnerSubscriptions?: boolean;
  ignoreStackFilterRe?: RegExp;
  subscribers?: Iterator;
}) {
  const sub = subscribers ?? getSubscribers();

  await delay(timeout);

  const current = sub.current();
  if (!current.length) {
    return;
  }

  // eslint-disable-next-line no-console
  console.error(prefix, 'Total subscriptions (including indirect/nested):', current.length);

  const map = new Set();
  const errors: { prefix: string; val: SubscriptionSource }[] = [];
  current.forEach((val) => {
    if (!reportInnerSubscriptions && map.has(val.id)) {
      return;
    }
    if ((rewriteStack || filterStackRe != null) && val.stack != null) {
      const frames = val.stack.split('\n');
      const stack =
        (filterStackRe != null &&
          frames
            .filter((it) => !it.includes('Observable.subscribe') && filterStackRe.test(it))
            .join('\n')) ||
        frames.join('\n');
      val.stack = stack;
    }
    errors.push({ prefix, val });
    map.add(val.id);
  });
  if (errors.length > 0) {
    if (ignoreStackFilterRe != null) {
      // group errors by id
      const groupByErrorId = errors.reduce((prev, cur) => {
        if (prev[cur.val.id] == null) {
          prev[cur.val.id] = [];
        }
        prev[cur.val.id].push({ prefix, val: cur.val });
        return prev;
      }, {} as { [key: string]: { prefix: string; val: SubscriptionSource }[] });

      // clear errors
      errors.length = 0;

      const ignored: { key: string; error: string }[] = [];
      // remove ignored groups
      for (const group in groupByErrorId) {
        if (group != null) {
          groupByErrorId[group].forEach((e) => {
            if (e.val.stack != null) {
              const matches = ignoreStackFilterRe.exec(e.val.stack);
              if (matches != null) {
                ignored.push({
                  key: matches[0],
                  error: e.val.stack,
                });
              }
            }
          });
          const canIgnoreGroup =
            groupByErrorId[group].find((e) =>
              e.val.stack != null ? ignoreStackFilterRe.test(e.val.stack) : false,
            ) != null;
          if (canIgnoreGroup) {
            delete groupByErrorId[group];
          }
        }
      }

      // flatten errors
      for (const group in groupByErrorId) {
        if (group != null) {
          errors.push(...groupByErrorId[group]);
        }
      }

      // create stats for ignored subscriptions
      const sorted = ignored.sort((a1, a2) => a1.key.localeCompare(a2.key));
      const normalized = sorted.slice().map((item) => {
        item.error = item.error.replace(/:\d+:\d+/g, '');
        return item;
      });
      const errorCache: { [key: string]: number } = {};
      const cache: { [key: string]: { errors: string[]; count: number } } = {};
      const filtered = normalized.filter((item) => {
        if (cache[item.key] == null) {
          errorCache[item.error] = 1;
          cache[item.key] = { errors: [item.error], count: 1 };
          return true;
        }
        if (errorCache[item.error] == null) {
          errorCache[item.error] = 1;
          cache[item.key].errors.push(item.error);
        }
        cache[item.key].count += 1;
        return false;
      });
      const stats = filtered
        .map((item) => {
          return {
            ignored: item.key,
            stack: cache[item.key].errors.join('\n'),
            count: cache[item.key].count,
            errors: cache[item.key].errors,
          };
        })
        .sort((a, b) => b.count - a.count);

      // output ignored unique subscription stats
      /* eslint-disable no-console */
      console.warn(prefix, 'Ignored unique subscriptions:', ignored.length);
      console.table(stats, ['ignored', 'stack', 'count']);
      /* eslint-enable no-console */
    }

    // output errors
    // eslint-disable-next-line no-console
    errors.forEach((e) => console.error(e.prefix, `#${e.val.id}:`, e.val));
  }
}
